<template>
  <div class="operator">
    <h1>Pending Withdrawals</h1>
    <div class="info-text">
      <em>
        This is a simulated Operator Portal page, showing disbursements that
        need to be approved, before the disbursement payment is made.
      </em>
    </div>
    <div v-if="isLoading" class="loading-spinner">Loading withdrawals...</div>
    <div v-else>
      <div v-if="pendingWithdraws.length === 0" class="no-withdrawals">
        <div class="empty-state">
          <h3>No Pending Withdrawals</h3>
          <p>All withdrawal requests have been processed.</p>
        </div>
      </div>
      <div v-else class="withdraw-list">
        <div class="list-header">
          <span class="count"
            >{{ pendingWithdraws.length }} pending withdrawal{{
              pendingWithdraws.length === 1 ? "" : "s"
            }}</span
          >
        </div>
        <div
          class="withdraw-card"
          v-for="withdraw in pendingWithdraws"
          :key="withdraw.externalDisbursementId"
        >
          <div class="card-header">
            <div class="amount-section">
              <span class="amount">${{ withdraw.amount }}</span>
              <span class="currency">{{ withdraw.currency }}</span>
            </div>
            <div class="status-section">
              <span
                class="status-badge"
                :class="getStatusClass(withdraw.status)"
              >
                {{ formatStatus(withdraw.status) }}
              </span>
              <span class="type-badge" :class="getTypeClass(withdraw.type)">
                {{ formatType(withdraw.type) }}
              </span>
            </div>
          </div>

          <div class="card-body">
            <div class="info-grid">
              <div class="info-item">
                <label>Customer ID</label>
                <span>{{ withdraw.user }}</span>
              </div>
              <div class="info-item">
                <label>Customer Name</label>
                <span
                  >{{ withdraw.customerData.firstName }}
                  {{ withdraw.customerData.lastName }}</span
                >
              </div>
              <div class="info-item">
                <label>Email</label>
                <span>{{ withdraw.customerData.email }}</span>
              </div>
              <div class="info-item">
                <label>Phone</label>
                <span>{{ withdraw.customerData.phone }}</span>
              </div>
              <div class="info-item">
                <label>Request ID</label>
                <span class="mono">{{ withdraw.externalDisbursementId }}</span>
              </div>
              <div class="info-item">
                <label>Disbursement ID</label>
                <span class="mono">{{ withdraw.disbursementIntentId }}</span>
              </div>
              <div class="info-item">
                <label>Submitted</label>
                <span>{{ formatDate(withdraw.submittedAt) }}</span>
                <small class="time-ago">{{
                  getTimeAgo(withdraw.submittedAt)
                }}</small>
              </div>
            </div>

            <div
              v-if="withdraw.errorMessages && withdraw.errorMessages.length > 0"
              class="error-messages"
            >
              <label>Issues</label>
              <ul>
                <li v-for="error in withdraw.errorMessages" :key="error">
                  {{ error }}
                </li>
              </ul>
            </div>
          </div>

          <div class="card-actions">
            <button
              @click="confirmAction(withdraw, 'approve')"
              class="action-btn approve-btn"
              :disabled="
                processingIds.includes(withdraw.externalDisbursementId)
              "
            >
              <span
                v-if="processingIds.includes(withdraw.externalDisbursementId)"
                >Processing...</span
              >
              <span v-else>✓ Approve</span>
            </button>
            <button
              @click="confirmAction(withdraw, 'reject')"
              class="action-btn reject-btn"
              :disabled="
                processingIds.includes(withdraw.externalDisbursementId)
              "
            >
              <span
                v-if="processingIds.includes(withdraw.externalDisbursementId)"
                >Processing...</span
              >
              <span v-else>✗ Reject</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="message" class="message" :class="messageType">
      {{ message }}
    </div>

    <!-- Confirmation Modal -->
    <div
      v-if="showConfirmation"
      class="modal-overlay"
      @click="cancelConfirmation"
    >
      <div class="modal" @click.stop>
        <h3>Confirm {{ confirmationAction }}</h3>
        <p>
          Are you sure you want to {{ confirmationAction.toLowerCase() }} this
          withdrawal?
        </p>
        <div class="withdrawal-summary">
          <strong
            >${{ selectedWithdraw?.amount }}
            {{ selectedWithdraw?.currency }}</strong
          >
          <br />
          Customer: {{ selectedWithdraw?.user }}
        </div>
        <div class="modal-actions">
          <button
            @click="cancelConfirmation"
            class="cancel-btn"
            :disabled="isProcessingAction"
          >
            Cancel
          </button>
          <button
            @click="executeAction"
            class="confirm-btn"
            :class="confirmationAction === 'Approve' ? 'approve' : 'reject'"
            :disabled="isProcessingAction"
          >
            <span v-if="isProcessingAction">Processing...</span>
            <span v-else>{{ confirmationAction }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { getPendingWithdraws, proceedDisbursement } from "@/api/vivamo-api";

export default {
  name: "Operator",
  setup() {
    const pendingWithdraws = ref([]);
    const isLoading = ref(true);
    const message = ref("");
    const messageType = ref("");
    const processingIds = ref([]);

    // Confirmation modal state
    const showConfirmation = ref(false);
    const selectedWithdraw = ref(null);
    const confirmationAction = ref("");
    const isProcessingAction = ref(false);

    const fetchPendingWithdraws = async () => {
      try {
        const data = await getPendingWithdraws();
        pendingWithdraws.value = data.map((item) => ({
          externalDisbursementId: item.externalDisbursementId,
          disbursementIntentId: item.disbursementIntentId,
          amount: item.amount.totalAmount,
          currency: item.currency,
          type: item.type,
          status: item.status.name,
          submittedAt: item.createdAt,
          user: item.externalCustomerId,
          customerData: {
            firstName: item.customerId?.data?.firstName || "",
            lastName: item.customerId?.data?.lastName || "",
            email: item.customerId?.data?.email || "",
            phone: item.customerId?.data?.phone || "",
          },
          errorMessages: item.status.errorMessages || [],
        }));
      } catch (error) {
        console.error("Error fetching pending withdraws:", error);
        showMessage(`Error loading withdrawals: ${error.message}`, "error");
      } finally {
        isLoading.value = false;
      }
    };

    const showMessage = (text, type = "info") => {
      message.value = text;
      messageType.value = type;
      setTimeout(() => {
        message.value = "";
        messageType.value = "";
      }, 5000);
    };

    const formatStatus = (status) => {
      return status
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
    };

    const formatType = (type) => {
      return type.charAt(0).toUpperCase() + type.slice(1);
    };

    const getStatusClass = (status) => {
      return `status-${status.replace(/[_-]/g, "-")}`;
    };

    const getTypeClass = (type) => {
      return `type-${type}`;
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    };

    const getTimeAgo = (dateString) => {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return "just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    };

    const confirmAction = (withdraw, action) => {
      selectedWithdraw.value = withdraw;
      confirmationAction.value =
        action.charAt(0).toUpperCase() + action.slice(1);
      showConfirmation.value = true;
    };

    const cancelConfirmation = () => {
      showConfirmation.value = false;
      selectedWithdraw.value = null;
      confirmationAction.value = "";
      isProcessingAction.value = false;
    };

    const executeAction = async () => {
      if (!selectedWithdraw.value) return;

      const approve = confirmationAction.value === "Approve";
      const withdrawId = selectedWithdraw.value.disbursementIntentId;
      const externalId = selectedWithdraw.value.externalDisbursementId;

      // Set loading state
      isProcessingAction.value = true;
      processingIds.value.push(externalId);

      try {
        await proceedDisbursement(withdrawId, approve);
        pendingWithdraws.value = pendingWithdraws.value.filter(
          (withdraw) => withdraw.disbursementIntentId !== withdrawId
        );
        showMessage(
          `Successfully ${approve ? "approved" : "rejected"} withdrawal for $${
            selectedWithdraw.value.amount
          }`,
          "success"
        );
      } catch (error) {
        console.error(
          `Error ${approve ? "approving" : "rejecting"} withdrawal:`,
          error
        );
        showMessage(
          `Error ${approve ? "approving" : "rejecting"} withdrawal: ${
            error.message
          }`,
          "error"
        );
      } finally {
        processingIds.value = processingIds.value.filter(
          (id) => id !== externalId
        );
        selectedWithdraw.value = null;
        confirmationAction.value = "";
        isProcessingAction.value = false;
        showConfirmation.value = false;
      }
    };

    onMounted(() => {
      fetchPendingWithdraws();
    });

    return {
      pendingWithdraws,
      isLoading,
      message,
      messageType,
      processingIds,
      showConfirmation,
      selectedWithdraw,
      confirmationAction,
      isProcessingAction,
      formatStatus,
      formatType,
      getStatusClass,
      getTypeClass,
      formatDate,
      getTimeAgo,
      confirmAction,
      cancelConfirmation,
      executeAction,
    };
  },
};
</script>

<style scoped>
.operator {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, sans-serif;
}

.operator h1 {
  margin-bottom: 10px;
}

.info-text {
  padding: 20px 0;
  color: var(--accent-color);
  margin-bottom: 30px;
}

.loading-spinner {
  text-align: center;
  padding: 40px;
  font-size: 18px;
  color: var(--text-light);
}

.no-withdrawals {
  display: flex;
  justify-content: center;
  padding: 60px 20px;
}

.empty-state {
  text-align: center;
  color: var(--text-light);
}

.empty-state h3 {
  margin-bottom: 10px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0 10px;
}

.count {
  font-weight: 600;
  font-size: 16px;
}

.withdraw-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.withdraw-card {
  background: var(--card-background);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  overflow: hidden;
}

.withdraw-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(255, 255, 255, 0.03);
}

.amount-section {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.amount {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-color);
}

.currency {
  font-size: 16px;
  color: var(--text-color);
  opacity: 0.7;
  font-weight: 500;
}

.status-section {
  display: flex;
  gap: 8px;
  align-items: center;
}

.status-badge,
.type-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.status-pending-validation {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.type-badge {
  background: #e3f2fd;
  color: #1565c0;
  border: 1px solid #bbdefb;
}

.type-bank {
  background: #e8f5e8;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
}

.card-body {
  padding: 20px 24px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-color);
  opacity: 0.6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-item span {
  font-size: 14px;
  color: var(--text-color);
  font-weight: 500;
}

.mono {
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  background: rgba(255, 255, 255, 0.05);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: var(--text-color);
  opacity: 0.8;
}

.time-ago {
  color: var(--text-color);
  opacity: 0.6;
  font-size: 11px;
  margin-top: 2px;
}

.error-messages {
  margin-top: 16px;
  padding: 12px;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 6px;
}

.error-messages label {
  font-size: 12px;
  font-weight: 600;
  color: #c53030;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  display: block;
}

.error-messages ul {
  margin: 0;
  padding-left: 16px;
}

.error-messages li {
  color: #c53030;
  font-size: 13px;
  margin-bottom: 4px;
}

.card-actions {
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.03);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.action-btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.approve-btn {
  background: #10b981;
  color: white;
  border: 2px solid #10b981;
}

.approve-btn:hover:not(:disabled) {
  background: #059669;
  border-color: #059669;
  transform: translateY(-1px);
}

.reject-btn {
  background: #ef4444;
  color: white;
  border: 2px solid #ef4444;
}

.reject-btn:hover:not(:disabled) {
  background: #dc2626;
  border-color: #dc2626;
  transform: translateY(-1px);
}

.message {
  margin-top: 20px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

.message.success {
  background: #d1fae5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.message.error {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fca5a5;
}

.message.info {
  background: #dbeafe;
  color: #1e40af;
  border: 1px solid #93c5fd;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: var(--card-background);
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.modal h3 {
  margin: 0 0 16px;
  color: var(--text-color);
  font-size: 18px;
}

.modal p {
  margin: 0 0 16px;
  color: var(--text-color);
  opacity: 0.8;
  line-height: 1.5;
}

.withdrawal-summary {
  background: rgba(255, 255, 255, 0.05);
  padding: 16px;
  border-radius: 8px;
  margin: 16px 0;
  text-align: center;
  border-left: 4px solid #3182ce;
  color: var(--text-color);
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
}

.cancel-btn {
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-color);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s ease;
}

.cancel-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.15);
}

.cancel-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.confirm-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  color: white;
  transition: background 0.2s ease;
}

.confirm-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.confirm-btn.approve {
  background: #10b981;
}

.confirm-btn.approve:hover:not(:disabled) {
  background: #059669;
}

.confirm-btn.reject {
  background: #ef4444;
}

.confirm-btn.reject:hover:not(:disabled) {
  background: #dc2626;
}

/* Enhanced mobile responsive styles */
@media (max-width: 768px) {
  .operator {
    padding: 1rem;
    max-width: 100%;
  }

  .operator h1 {
    font-size: 1.8rem;
    text-align: center;
    margin-bottom: 0.8rem;
    color: var(--text-light);
  }

  .info-text {
    padding: 1rem 0;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
    line-height: 1.4;
    text-align: center;
  }

  .loading-spinner {
    padding: 2rem;
    font-size: 1rem;
  }

  .no-withdrawals {
    padding: 3rem 1rem;
  }

  .empty-state h3 {
    font-size: 1.2rem;
    margin-bottom: 0.8rem;
  }

  .empty-state p {
    font-size: 0.9rem;
  }

  .list-header {
    margin-bottom: 1rem;
    padding: 0 0.5rem;
  }

  .count {
    font-size: 0.9rem;
  }

  .withdraw-list {
    gap: 1rem;
  }

  .withdraw-card {
    border-radius: 8px;
    margin: 0 -0.5rem;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.8rem;
    padding: 1rem;
  }

  .amount-section {
    width: 100%;
    justify-content: space-between;
  }

  .amount {
    font-size: 1.6rem;
  }

  .currency {
    font-size: 0.9rem;
  }

  .status-section {
    gap: 0.5rem;
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .status-badge,
  .type-badge {
    padding: 0.25rem 0.75rem;
    font-size: 0.7rem;
  }

  .card-body {
    padding: 1rem;
  }

  .info-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .info-item label {
    font-size: 0.7rem;
  }

  .info-item span {
    font-size: 0.8rem;
  }

  .mono {
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
  }

  .time-ago {
    font-size: 0.65rem;
  }

  .error-messages {
    padding: 0.8rem;
    margin-top: 1rem;
  }

  .error-messages label {
    font-size: 0.7rem;
  }

  .error-messages li {
    font-size: 0.75rem;
  }

  .card-actions {
    flex-direction: column;
    gap: 0.8rem;
    padding: 1rem;
  }

  .action-btn {
    padding: 0.8rem 1rem;
    font-size: 0.8rem;
    min-height: 40px;
  }

  .message {
    margin-top: 1rem;
    padding: 0.8rem 1rem;
    font-size: 0.8rem;
  }

  .modal {
    margin: 1rem;
    padding: 1.5rem;
    max-width: none;
    width: calc(100% - 2rem);
  }

  .modal h3 {
    font-size: 1.1rem;
  }

  .modal p {
    font-size: 0.9rem;
  }

  .withdrawal-summary {
    padding: 1rem;
    font-size: 0.9rem;
  }

  .modal-actions {
    flex-direction: column-reverse;
    gap: 0.8rem;
    margin-top: 1.5rem;
  }

  .cancel-btn,
  .confirm-btn {
    padding: 0.8rem 1rem;
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .operator {
    padding: 0.75rem;
  }

  .operator h1 {
    font-size: 1.6rem;
    margin-bottom: 0.6rem;
  }

  .info-text {
    padding: 0.8rem 0;
    margin-bottom: 1.2rem;
    font-size: 0.85rem;
  }

  .loading-spinner {
    padding: 1.5rem;
    font-size: 0.9rem;
  }

  .no-withdrawals {
    padding: 2rem 0.5rem;
  }

  .empty-state h3 {
    font-size: 1.1rem;
  }

  .empty-state p {
    font-size: 0.85rem;
  }

  .list-header {
    padding: 0;
  }

  .count {
    font-size: 0.85rem;
  }

  .withdraw-list {
    gap: 0.8rem;
  }

  .withdraw-card {
    margin: 0;
    border-radius: 6px;
  }

  .card-header {
    padding: 0.8rem;
    gap: 0.6rem;
  }

  .amount {
    font-size: 1.4rem;
  }

  .currency {
    font-size: 0.8rem;
  }

  .status-badge,
  .type-badge {
    padding: 0.2rem 0.6rem;
    font-size: 0.65rem;
  }

  .card-body {
    padding: 0.8rem;
  }

  .info-grid {
    gap: 0.8rem;
  }

  .info-item label {
    font-size: 0.65rem;
  }

  .info-item span {
    font-size: 0.75rem;
  }

  .mono {
    font-size: 0.65rem;
    padding: 0.2rem 0.4rem;
  }

  .time-ago {
    font-size: 0.6rem;
  }

  .error-messages {
    padding: 0.6rem;
  }

  .card-actions {
    padding: 0.8rem;
    gap: 0.6rem;
  }

  .action-btn {
    padding: 0.7rem 0.8rem;
    font-size: 0.75rem;
    min-height: 36px;
  }

  .modal {
    margin: 0.5rem;
    padding: 1.2rem;
  }

  .modal h3 {
    font-size: 1rem;
  }

  .modal p {
    font-size: 0.85rem;
  }

  .withdrawal-summary {
    padding: 0.8rem;
    font-size: 0.85rem;
  }

  .cancel-btn,
  .confirm-btn {
    padding: 0.7rem 0.8rem;
    font-size: 0.85rem;
  }
}

/* Very small screens */
@media (max-width: 360px) {
  .operator {
    padding: 0.5rem;
  }

  .operator h1 {
    font-size: 1.4rem;
  }

  .info-text {
    font-size: 0.8rem;
  }

  .amount {
    font-size: 1.2rem;
  }

  .currency {
    font-size: 0.75rem;
  }

  .status-section {
    width: 100%;
  }

  .status-badge,
  .type-badge {
    flex: 1;
    text-align: center;
    padding: 0.15rem 0.4rem;
    font-size: 0.6rem;
  }

  .card-header,
  .card-body,
  .card-actions {
    padding: 0.6rem;
  }

  .info-item span {
    font-size: 0.7rem;
  }

  .action-btn {
    padding: 0.6rem;
    font-size: 0.7rem;
    min-height: 32px;
  }

  .modal {
    margin: 0.25rem;
    padding: 1rem;
  }
}

/* Landscape mobile orientation */
@media (max-width: 768px) and (orientation: landscape) {
  .operator h1 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  .info-text {
    padding: 0.5rem 0;
    margin-bottom: 1rem;
    font-size: 0.8rem;
  }

  .withdraw-list {
    gap: 0.8rem;
  }

  .card-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0.8rem;
    gap: 1rem;
  }

  .amount-section {
    width: auto;
  }

  .amount {
    font-size: 1.3rem;
  }

  .card-body {
    padding: 0.8rem;
  }

  .info-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.8rem;
  }

  .card-actions {
    flex-direction: row;
    padding: 0.8rem;
    gap: 0.6rem;
  }

  .action-btn {
    flex: 1;
    padding: 0.6rem 0.8rem;
    font-size: 0.75rem;
    min-height: 36px;
  }

  .modal {
    max-width: 400px;
    width: 80%;
    margin: 1rem;
  }

  .modal-actions {
    flex-direction: row;
    justify-content: flex-end;
  }
}
</style>
